import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { LegalAdviceFormFiveData } from "../types/LegalAdvices/LegalAdviceFormFive.type";

const ENDPOINT = "legal-advice-form-five";

export interface LegalAdviceFormFiveFetchResponse {
  data: LegalAdviceFormFiveData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Five
// ---------------------------------------------------------------------------
export const mapLegalAdviceFormFiveBackendToFrontend = (
  item: any
): LegalAdviceFormFiveData => {
  if (!item) return {} as LegalAdviceFormFiveData;

  const form = item.legalAdviceFormFive || item.formData || item;

  return {
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    formType: item.formType || 'LEGAL_ADVICE_FORM_FIVE',
    status: item.status || form.status || 'DRAFT',

    // Form-Specific Fields
    office: form.office || "",
    township: form.township || "",
    courtName: form.courtName || "",
    appealNo: form.appealNo || "",
    appellantName: form.appellantName || "",
    respondentName: form.respondentName || "",
    originalCourtDetails: form.originalCourtDetails || "",
    appellateCourtDetails: form.appellateCourtDetails || "",
    intermediateCourtDetails: form.intermediateCourtDetails || "",
    advocateSubmission: form.advocateSubmission || "",
    advocateOpinion: form.advocateOpinion || "",
    advocateSignName: form.advocateSignName || "",
    advocateSignDate: form.advocateSignDate || null,
    responsibleOfficerOpinion: form.responsibleOfficerOpinion || "",
    responsibleOfficerSignName: form.responsibleOfficerSignName || "",
    responsibleOfficerSignDate: form.responsibleOfficerSignDate || null,
    stateRegionOpinion: form.stateRegionOpinion || "",
    stateRegionSignName: form.stateRegionSignName || "",
    stateRegionSignDate: form.stateRegionSignDate || null,
    signatureImageAdvocate: formatSignatureUrl(form.signatureImageAdvocate),
    signatureImageOfficer: formatSignatureUrl(form.signatureImageOfficer),
    signatureImageStateRegion: formatSignatureUrl(form.signatureImageStateRegion),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Five
// ---------------------------------------------------------------------------
const legalAdviceFormFiveFilterPredicate = (
  app: LegalAdviceFormFiveData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate ||
    Boolean(app.submitDate && app.submitDate.startsWith(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.appealNo && app.appealNo.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(app.appellantName && app.appellantName.toLowerCase().includes(term)) ||
    Boolean(app.respondentName && app.respondentName.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

// Fetch All / Filtered
export const fetchLegalAdviceFormFiveApplications = (
  params: FetchParams
): Promise<LegalAdviceFormFiveFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormFiveData>(
    ENDPOINT,
    params,
    mapLegalAdviceFormFiveBackendToFrontend,
    legalAdviceFormFiveFilterPredicate
  );

// Fetch Single Application by ID
export const fetchLegalAdviceFormFiveById = (
  id: string
): Promise<LegalAdviceFormFiveData> =>
  genericService.fetchApplicationById<LegalAdviceFormFiveData>(
    ENDPOINT,
    id,
    mapLegalAdviceFormFiveBackendToFrontend
  );

// Save Draft
export const saveLegalAdviceFormFiveDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormFiveData>
) => genericService.saveDraft<LegalAdviceFormFiveData>(ENDPOINT, id, data);

// Submit Form
export const submitLegalAdviceFormFiveForm = (
  id: string,
  data: LegalAdviceFormFiveData
) => genericService.submitForm<LegalAdviceFormFiveData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deleteLegalAdviceFormFiveForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------
export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);